// ─── Recherche d'un produit par code-barres (bases publiques gratuites) ──────
// Détection automatique selon le code scanné :
//   • ISBN (EAN-13 préfixe 978/979) → livres/manga : Google Books, repli Open Library.
//   • sinon → alimentaire : Open Food Facts.
// Aucune clé d'API. Tout par simple fetch (web + natif).
//
//   const r = await chercherParCodeBarres('9782505011385');
//   → { trouve, code, nom, marque?, auteur?, photoUrl?, source }
import { chercherProduit, codeBarresValide } from './openFoodFacts';

// Un ISBN-13 est un EAN-13 commençant par 978 ou 979.
export function estISBN(code) {
  const c = String(code || '').replace(/[^0-9]/g, '');
  return c.length === 13 && (c.startsWith('978') || c.startsWith('979'));
}

// ── Livres / manga ─────────────────────────────────────────────────────────
async function chercherLivre(isbn) {
  // 1) Google Books (bonne couverture manga, titres FR).
  try {
    const r = await fetch(`https://www.googleapis.com/books/v1/volumes?q=isbn:${isbn}&country=FR`);
    if (r.ok) {
      const j = await r.json();
      const v = j && j.items && j.items[0] && j.items[0].volumeInfo;
      if (v && v.title) {
        const nom = v.subtitle ? `${v.title} — ${v.subtitle}` : v.title;
        const img = v.imageLinks ? (v.imageLinks.thumbnail || v.imageLinks.smallThumbnail || '') : '';
        return {
          trouve: true, source: 'google-books', code: isbn,
          nom,
          auteur:  Array.isArray(v.authors) ? v.authors.join(', ') : '',
          editeur: v.publisher || '',
          annee:   (v.publishedDate || '').slice(0, 4) || null,
          pages:   v.pageCount || null,
          photoUrl: img ? img.replace(/^http:\/\//, 'https://') : null,
        };
      }
    }
  } catch (_) { /* on tente Open Library */ }

  // 2) Open Library (repli : titre + couverture par ISBN).
  try {
    const r = await fetch(`https://openlibrary.org/isbn/${isbn}.json`);
    if (r.ok) {
      const j = await r.json();
      const titre = (j && j.title) ? j.title : '';
      if (titre) {
        return {
          trouve: true, source: 'open-library', code: isbn,
          nom: j.subtitle ? `${titre} — ${j.subtitle}` : titre, auteur: '',
          editeur: Array.isArray(j.publishers) ? (j.publishers[0] || '') : '',
          annee: (String(j.publish_date || '').match(/\d{4}/) || [])[0] || null,
          pages: j.number_of_pages || null,
          photoUrl: `https://covers.openlibrary.org/b/isbn/${isbn}-M.jpg`,
        };
      }
    }
  } catch (_) { /* rien trouvé */ }

  return { trouve: false, code: isbn, source: 'livre' };
}

// ── Alimentaire (Open Food Facts) ───────────────────────────────────────────
async function chercherAlimentaire(code) {
  const p = await chercherProduit(code);
  if (!p || !p.trouve) return { trouve: false, code, source: 'off' };
  return {
    trouve: true, source: 'off', code,
    nom: p.nom || '', marque: p.marque || '', photoUrl: p.photoUrl || null,
  };
}

// ── Recherche unifiée ───────────────────────────────────────────────────────
// Renvoie toujours un objet (jamais d'exception) : { trouve, ... }.
export async function chercherParCodeBarres(code) {
  const c = String(code || '').replace(/[^0-9]/g, '');
  if (!codeBarresValide(c)) return { trouve: false, code: c, raison: 'code' };
  try {
    if (estISBN(c)) {
      const livre = await chercherLivre(c);
      if (livre.trouve) return livre;
      // ISBN non trouvé côté livres : on tente quand même l'alimentaire (rare).
    }
    return await chercherAlimentaire(c);
  } catch (_) {
    return { trouve: false, code: c, raison: 'reseau' };
  }
}
